import { PROJECT_LAYER_LABELS, loadingLayers, type Project } from "../../types/project";

export interface ProjectStatusInfo {
  isProcessing: boolean;
  isExpanding: boolean;
  isRefreshing: boolean;
  isFailed: boolean;
  /** The project cannot be opened right now. */
  isBusy: boolean;
  /** Short status for a chip, or null when the project is simply ready. */
  label: string | null;
  tone: "neutral" | "error";
  /** Layers still loading behind an otherwise open project ("Loading weather
   *  history, assets…"), or null when everything is in. */
  backgroundLabel: string | null;
}

/** "Loading weather history, assets…" for the layers still on their way. */
export function describeLoadingLayers(project: Project): string | null {
  const names = loadingLayers(project.layer_status).map((l) => PROJECT_LAYER_LABELS[l].toLowerCase());
  if (names.length === 0) return null;
  const shown = names.slice(0, 2).join(", ");
  return names.length > 2 ? `loading ${shown} +${names.length - 2}…` : `loading ${shown}…`;
}

/** The status wording shared by the cards and the table rows. */
export function deriveProjectStatus(project: Project): ProjectStatusInfo {
  const isProcessing = project.status === "processing" && !!project.task_id;
  const isExpanding = project.status === "expanding" && !!project.task_id;
  const isRefreshing = project.status === "refreshing";
  // Initialization failed: no usable layers, so the project can't be opened;
  // only archiving remains available (#184).
  const isFailed = project.status === "failed";
  let label: string | null = null;
  if (isFailed) label = "Initialization failed";
  else if (isProcessing) {
    label =
      project.init_tiles_total && project.init_tiles_total > 1
        ? `Initializing ${project.init_tiles_completed ?? 0} of ${project.init_tiles_total} tiles…`
        : "Initializing…";
  } else if (isExpanding) label = "Expanding…";
  else if (isRefreshing) label = "Refreshing data…";
  // Once the project is open, anything still loading is a second, quieter
  // chip: the project is usable while weather, assets and the rest arrive.
  const backgroundLabel =
    !isFailed && !isProcessing && project.status === "ready" ? describeLoadingLayers(project) : null;
  return {
    isProcessing,
    isExpanding,
    isRefreshing,
    isFailed,
    isBusy: isProcessing || isFailed,
    label,
    tone: isFailed ? "error" : "neutral",
    backgroundLabel,
  };
}
